import { Component, ElementRef, Input, ViewChild, forwardRef, inject } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { forkJoin, map } from 'rxjs';
import { FileService } from 'src/app/core/services/files.service';
import { AlertModalComponent } from '../alert-modal/alert-modal.component';

@Component({
  selector: 'nobis-input-type-file',
  templateUrl: './input-type-file.component.html',
  styleUrls: ['./input-type-file.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => InputTypeFileComponent),
      multi: true,
    },
  ],
})
export class InputTypeFileComponent implements ControlValueAccessor {
  @Input() status!: 1 | 0;
  @Input() label!: string;
  @Input() multi = true;
  @Input() hint = ' Admite .jpg, .png, .heic, .pdf,<strong> hasta 5 MB y 5 archivos.</strong>';
  @Input() size = 4;
  @Input() filesLimit = 5;
  @Input() disabled? = false;
  @Input() maxFileSizeKilobytes = 5000;
  private fileService = inject(FileService);
  private dialog = inject(MatDialog);
  private acceptTypes = ['application/pdf', 'image/png', 'image/jpeg', 'image/heic', 'image/heif'];
  protected selectedFiles: File[] = [];
  protected accept = this.acceptTypes.join(',');
  protected color = '#D9D9D9';

  @ViewChild('fileInput') fileInput!: ElementRef;

  protected onFileSelected(event: Event): void {
    const fileList = (event.target as HTMLInputElement).files;    

    if (fileList!.length > this.filesLimit) {
      this.showModal(`La cantidad de archivos adjuntos no puede ser mayor a ${this.filesLimit}.`);
      return;
    }

    this.inputInvalid();

    if (this.isFileUploadValid(fileList)) {
      this.selectedFiles = [];
      return;
    }

    const fileExtensionException = ['heic', 'heif'];

    Array.from(fileList!).forEach((file: File) => {
      const fragmentedName = file.name.split('.');
      const baseName = `${fragmentedName[fragmentedName.length - 2]}`;
      const fileExtension = fragmentedName[fragmentedName.length - 1];      
      const fileNameWithLowerExt = `${baseName}.${fileExtension.toLowerCase()}`; // Nombre con extensión en minúsculas: "archivo.PDF" → "archivo.pdf"
      
      if (
        !fileExtensionException.some(exception => exception === fileExtension.toLowerCase()) &&
        !this.fileService.isFileTypeAcceptable(this.acceptTypes, file.type)
      ) {
        this.showModal('Los archivos adjuntos deben tener formato .jpg, .png o .pdf.');
        return;
      }
      
      const maxSizeToMB = this.maxFileSizeKilobytes / 1000;
      if (!this.fileService.isFileSizeAcceptable(this.maxFileSizeKilobytes, file.size)) {
        this.showModal(`Los archivos adjuntos no pueden superar los ${maxSizeToMB}MB de tamaño.`);
        return;
      }
      

      const fileWithLowerExt = new File([file], fileNameWithLowerExt, {
        type: file.type,
        lastModified: file.lastModified,
      });

      if (this.selectedFiles.length === this.filesLimit) {
        this.showModal(`La cantidad de archivos adjuntos no puede ser mayor a ${this.filesLimit}.`);
        return;
      }
      this.listFile(fileWithLowerExt);
    });

    this.fileInput.nativeElement.value = null;
  }

  protected listFile(file: File) {
    this.selectedFiles.push(file);

    this.updateBase64();
  }

  private updateBase64(): void {

    const files = this.selectedFiles.map(file =>
      this.fileService.fileToBase64(file).pipe(
        map(base64 => ({
          name: file.name,
          base64,
        }))
      )
    );

    forkJoin(files).subscribe({
      next: args => {
        this.onChange(args);
      },
    });
  }

  protected togglePasswordVisibility(event: Event): void {
    event?.stopPropagation();
  }

  protected removeFile(index: number): void {
    if (index < 0) {
      return;
    }

    this.selectedFiles.splice(index, 1);
    this.onChange(this.selectedFiles);
  }

  protected downloadDocument(file: File): void {
    this.fileService.downloadFile(file, file.name, file.type);
  }

  protected inputInvalid(): boolean {
    return this.selectedFiles.length > this.size || this.selectedFiles.length === this.filesLimit;
  }

  private isFileUploadValid(fileList: FileList | null): boolean {
    return !fileList || !fileList.length || fileList.length > 5;
  }

  private _value: any;

  get value(): any {
    return this._value;
  }

  set value(val: any) {
    this._value = val;
    this.onChange(val);
    this.onTouched();
  }

  // Métodos de ControlValueAccessor
  writeValue(value: any): void {
    this._value = value;
    // Si el modelo se resetea (null / vacío), se limpia también la vista (archivos y el input nativo).
    const isEmpty = value === null || value === undefined || (Array.isArray(value) && !value.length);
    if (isEmpty) {
      this.selectedFiles = [];
      if (this.fileInput) this.fileInput.nativeElement.value = null;
    }
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  private onChange = (_: any) => {};
  private onTouched = () => {};

  protected showModal(message: string) {
    this.dialog.open(AlertModalComponent, {
      data: {
        type: 'error',
        text: message
      }
    });
  }
}
